// src/App.tsx

import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { FileSpreadsheet, User } from 'lucide-react';
import { HomePage } from './components/HomePage';
import { ProductPage } from './components/ProductPage';
import { SignIn } from './components/SignIn';
import { DownloadPage } from './components/DownloadPage';
import { Products } from './components/Products';
import { Tutorials } from './components/Tutorials';
import { Community } from './components/Community';
import { AdminPage } from './components/AdminPage';
import { SuccessPage } from './components/SuccessPage'; // Import the SuccessPage component
import { Seo } from './components/Seo';
import { isExcelAfterDark, siteConfig } from './config/site';
import { FreeResourcePage } from './components/FreeResourcePage';
import { Blog } from './components/Blog';

const App = () => {
  const navigate = useNavigate();
  const [isAdmin] = useState(import.meta.env.VITE_ENABLE_ADMIN_UI === 'true');

  return (
    <div className={`min-h-screen ${isExcelAfterDark ? 'ead-site' : 'bg-gradient-to-b from-gray-50 to-white'}`}>
      <Seo />
      {/* Header */}
      <header className={siteConfig.theme.header}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo and Title */}
          <Link to="/" className="flex items-center space-x-2 no-underline hover:no-underline">
            <FileSpreadsheet className="h-8 w-8 text-white" />
            <span className="text-2xl font-bold text-white hover:text-white">{siteConfig.name}</span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/products" className="text-white hover:text-gray-200 no-underline">Products</Link>
            {!isExcelAfterDark && <Link to="/tutorials" className="text-white hover:text-gray-200 no-underline">Tutorials</Link>}
            {!isExcelAfterDark && <Link to="/community" className="text-white hover:text-gray-200 no-underline">Community</Link>}
            {isExcelAfterDark && (
              <a href={siteConfig.trainingUrl} className="text-white hover:text-gray-200 no-underline">Training</a>
            )}
            {isExcelAfterDark && <Link to="/free/after-hours-starter-kit" className="text-white hover:text-gray-200 no-underline">Free resources</Link>}
            {isExcelAfterDark && <Link to="/blog" className="text-white hover:text-gray-200 no-underline">Journal</Link>}
            {isExcelAfterDark && <Link to="/community" className="text-white hover:text-gray-200 no-underline">Community</Link>}
            {isAdmin && (
              <Link to="/admin" className="text-white hover:text-gray-200 no-underline flex items-center">
                <User className="h-5 w-5 mr-1" />
                Admin
              </Link>
            )}
            {!isExcelAfterDark && (
              <button
                onClick={() => navigate('/signin')}
                className={`bg-white ${siteConfig.theme.buttonText} px-4 py-2 rounded-lg hover:bg-gray-100`}
              >
                Sign In
              </button>
            )}
            {isExcelAfterDark && (
              <Link to="/products/excel-colour-picker" className="border border-pink-500 text-white px-4 py-2 rounded-lg hover:bg-pink-500/10 no-underline">
                Explore the Colour Picker
              </Link>
            )}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main>
        <Routes>
          {/* Home Route */}
          <Route path="/" element={<HomePage />} />

          {/* Product Route */}
          <Route path="/products/:slug" element={<ProductPage />} />

          {/* Success Route */}
          <Route path="/success" element={<SuccessPage />} />

          {/* Other Routes */}
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/products" element={<Products />} />
          <Route path="/tutorials" element={<Tutorials />} />
          <Route path="/community" element={<Community />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          <Route path="/free/:slug" element={<FreeResourceRoute />} />
          <Route path="/admin" element={<AdminPage />} />

          {/* Fallback Route for 404 Not Found */}
          <Route path="*" element={<div className="text-center py-16">Page not found</div>} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FileSpreadsheet className={`h-6 w-6 ${siteConfig.theme.icon}`} />
                <span className="text-lg font-bold">{siteConfig.name}</span>
              </div>
              <p className="text-gray-400">{siteConfig.footer}</p>
            </div>

            {/* Products Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-gray-400">
                {isExcelAfterDark ? <li><Link to="/products/excel-colour-picker" className="hover:text-white no-underline">Excel Colour Picker</Link></li> : <>
                  <li><Link to="/products#addins" className="hover:text-white no-underline">Add-ins</Link></li>
                  <li><Link to="/products#templates" className="hover:text-white no-underline">Templates</Link></li>
                  <li><Link to="/products#custom" className="hover:text-white no-underline">Custom Solutions</Link></li>
                  <li><Link to="/products#enterprise" className="hover:text-white no-underline">Enterprise</Link></li>
                </>}
              </ul>
            </div>

            {/* Resources Links */}
            <div className={isExcelAfterDark ? 'hidden' : ''}>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/tutorials" className="hover:text-white no-underline">Tutorials</Link></li>
                <li><Link to="/docs" className="hover:text-white no-underline">Documentation</Link></li>
                <li><Link to="/community" className="hover:text-white no-underline">Community</Link></li>
                <li><Link to="/blog" className="hover:text-white no-underline">Blog</Link></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                {isExcelAfterDark ? <>
                  <li><a href={siteConfig.trainingUrl} className="hover:text-white no-underline">Excel Training</a></li>
                  <li><Link to="/free/after-hours-starter-kit" className="hover:text-white no-underline">Free resources</Link></li>
                  <li><Link to="/blog" className="hover:text-white no-underline">After-hours journal</Link></li>
                  <li><Link to="/community" className="hover:text-white no-underline">Community</Link></li>
                </> : <>
                  <li><Link to="/about" className="hover:text-white no-underline">About Us</Link></li>
                  <li><Link to="/contact" className="hover:text-white no-underline">Contact</Link></li>
                  <li><Link to="/privacy" className="hover:text-white no-underline">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="hover:text-white no-underline">Terms of Service</Link></li>
                </>}
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} {siteConfig.domain}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FreeResourceRoute = () => {
  const slug = window.location.pathname.split('/').pop() || '';
  return <FreeResourcePage slug={slug} />;
};

export default App;
